import React from 'react';

interface SimpleMarkdownRendererProps {
    content?: string;
    className?: string;
    emptyText?: string;
}

const renderInline = (text: string): React.ReactNode[] => {
    return text
        .split(/(\*\*.*?\*\*)/g)
        .filter(Boolean)
        .map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
                return <strong key={`${part}-${index}`} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
            }

            return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
        });
};

export const SimpleMarkdownRenderer: React.FC<SimpleMarkdownRendererProps> = ({
    content,
    className = '',
    emptyText = '暂无内容'
}) => {
    const normalized = (content || '').replace(/\r\n/g, '\n').trim();

    if (!normalized) {
        return <p className={`text-sm leading-relaxed text-gray-500 ${className}`}>{emptyText}</p>;
    }

    const lines = normalized.split('\n');
    const blocks: React.ReactNode[] = [];
    let index = 0;

    while (index < lines.length) {
        const line = lines[index].trim();

        if (!line) {
            index += 1;
            continue;
        }

        if (line.startsWith('## ')) {
            blocks.push(
                <h3 key={`heading-${index}`} className="text-sm font-bold text-gray-900 pt-1">
                    {renderInline(line.slice(3).trim())}
                </h3>
            );
            index += 1;
            continue;
        }

        if (/^-\s+/.test(line)) {
            const items: string[] = [];
            while (index < lines.length && /^-\s+/.test(lines[index].trim())) {
                items.push(lines[index].trim().replace(/^-\s+/, ''));
                index += 1;
            }

            blocks.push(
                <ul key={`ul-${index}`} className="list-disc pl-5 space-y-1 text-sm leading-relaxed text-gray-700">
                    {items.map((item, itemIndex) => (
                        <li key={`${item}-${itemIndex}`}>{renderInline(item)}</li>
                    ))}
                </ul>
            );
            continue;
        }

        if (/^\d+\.\s+/.test(line)) {
            const items: string[] = [];
            while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
                items.push(lines[index].trim().replace(/^\d+\.\s+/, ''));
                index += 1;
            }

            blocks.push(
                <ol key={`ol-${index}`} className="list-decimal pl-5 space-y-1 text-sm leading-relaxed text-gray-700">
                    {items.map((item, itemIndex) => (
                        <li key={`${item}-${itemIndex}`}>{renderInline(item)}</li>
                    ))}
                </ol>
            );
            continue;
        }

        const paragraphLines: string[] = [];
        while (
            index < lines.length &&
            lines[index].trim() &&
            !lines[index].trim().startsWith('## ') &&
            !/^-\s+/.test(lines[index].trim()) &&
            !/^\d+\.\s+/.test(lines[index].trim())
        ) {
            paragraphLines.push(lines[index].trim());
            index += 1;
        }

        blocks.push(
            <p key={`p-${index}`} className="text-sm leading-relaxed text-gray-700">
                {renderInline(paragraphLines.join(' '))}
            </p>
        );
    }

    return <div className={`space-y-3 ${className}`}>{blocks}</div>;
};

export default SimpleMarkdownRenderer;
